import type { FieldValidationState } from '@ramp106/omrjs-core-ui'
import type { BaseValidation } from '@vuelidate/core'
import { helpers } from '@vuelidate/validators'
import emailRegex from 'email-regex'
import { uniq } from 'lodash-es'
import phoneRegex from 'phone-regex'
import urlRegex from 'url-regex-safe'

import {
  validateImageSquare,
  validateRasterImageSquare,
  validateImageMinSize,
  validateRasterImageMinSize,
  validateImageMaxSize,
  validateFileMaxSize,
  validateFileMimeType,
} from '@/helpers/imageSizeHelper'
import { ALLOWED_LOCALES } from '@/helpers/translationsHelper'
import i18n from '@/plugins/i18n'

type TranslateFunction = typeof i18n.global.t

export const getValidationErrors = (validation: BaseValidation, opts: { externalErrors?: boolean } = { externalErrors: true }) => {
  const errors = []

  for (const error of validation.$errors) {
    if (error.$message) {
      errors.push(error.$message)
    }
  }

  if (opts.externalErrors && validation.$externalResults?.length) {
    for (const externalResult of validation.$externalResults) {
      if (externalResult.$message) {
        errors.push(externalResult.$message)
      }
    }
  }

  return errors.length ? uniq(errors).join(' ') : undefined
}

export const getValidationState = (validation: BaseValidation) => {
  const state: FieldValidationState = validation.$error ? 'invalid' : 'none'

  return {
    state,
    msg: getValidationErrors(validation),
  }
}

export const buildTranslationFieldsRules = <T>(rule: T, locales: string[] = ALLOWED_LOCALES): Record<string, T> => {
  return locales.reduce(
    (acc, locale) => {
      acc[locale] = rule
      return acc
    },
    {} as Record<string, T>,
  )
}

export const urlRule = (t: TranslateFunction) =>
  helpers.withMessage(t('validations.url').toString(), (value: string) => {
    if (!helpers.req(value)) return true
    return value.startsWith('http') && urlRegex({ exact: true }).test(value)
  })

export const imageSquareRule = helpers.withMessage('validations.imageNotSquare', helpers.withAsync(validateImageSquare))

// @deprecated use imageMinDimensionRule instead
export const imageMinSizeRule = (minWidth: number, minHeight: number) =>
  helpers.withMessage('validations.sizeTooSmall', helpers.withAsync(validateImageMinSize(minWidth, minHeight)))

// @deprecated use imageMaxDimensionRule instead
export const imageMaxSizeRule = (maxWidth: number, maxHeight: number) =>
  helpers.withMessage('validations.sizeTooBig', helpers.withAsync(validateImageMaxSize(maxWidth, maxHeight)))

export const imageMinDimensionRule = (t: TranslateFunction, params: { minWidth: number; minHeight: number }) =>
  helpers.withMessage(
    t('validations.imageMinDimension', params).toString(),
    helpers.withAsync(validateImageMinSize(params.minWidth, params.minHeight)),
  )
export const rasterImageMinDimensionRule = (t: TranslateFunction, params: { minWidth: number; minHeight: number }) =>
  helpers.withMessage(
    t('validations.imageMinDimension', params).toString(),
    helpers.withAsync(validateRasterImageMinSize(params.minWidth, params.minHeight)),
  )
export const imageMaxDimensionRule = (t: TranslateFunction, params: { maxWidth: number; maxHeight: number }) =>
  helpers.withMessage(
    t('validations.imageMaxDimension', params).toString(),
    helpers.withAsync(validateImageMaxSize(params.maxWidth, params.maxHeight)),
  )

export const rasterImageSquareDimensionRule = (t: TranslateFunction) =>
  helpers.withMessage(t('validations.imageNotSquare').toString(), helpers.withAsync(validateRasterImageSquare))

export const fileMaxSizeRule = (t: TranslateFunction, params: { megaBytes: number }) =>
  helpers.withMessage(t('validations.fileSizeTooBig', params).toString(), helpers.withAsync(validateFileMaxSize(params.megaBytes)))

export const fileMimeTypeRule = (t: TranslateFunction, params: { mimeTypes: string[] }) =>
  helpers.withMessage(t('validations.fileMimeType', params).toString(), helpers.withAsync(validateFileMimeType(params.mimeTypes)))

export const uniqueEmailRule = (t: TranslateFunction, params: { existingEmails: string[] }) =>
  helpers.withMessage(t('validations.uniqueEmail').toString(), (value: string) => {
    if (!helpers.req(value)) return true
    return !params.existingEmails.includes(value)
  })

export const validPhoneNumberRule = (t: TranslateFunction) =>
  helpers.withMessage(t('validations.phoneNumber').toString(), (value: string) => {
    if (!value.length) return true
    return phoneRegex({ exact: true }).test(value)
  })

export const urlWithSpecificDomainRule = (t: TranslateFunction, params: { domainNames: string[] }) =>
  helpers.withMessage(
    t('validations.urlWithSpecificDomain', { domainNames: params.domainNames.join(', ') }).toString(),
    (value: string) => {
      if (!helpers.req(value)) return true
      if (!urlRegex({ exact: true, strict: true }).test(value)) return false
      try {
        const url = new URL(value)
        if (url.protocol !== 'http:' && url.protocol !== 'https:') return false
        const host = url.hostname.replace(/^www\./, '')
        return params.domainNames.includes(host)
      } catch {
        return false
      }
    },
  )

export const linkedinUrlRule = (t: TranslateFunction) =>
  helpers.withMessage(t('validations.url').toString(), helpers.regex(/^https:\/\/(www|[a-z]{2,3})\.linkedin\.com\/.+$/))

export const containsEmailOrUrlRule = (t: TranslateFunction, params: { isActive: boolean; allowedTitle: string }) =>
  helpers.withMessage(t('validations.containsEmailOrUrl').toString(), (value: string) => {
    if (!params.isActive) return true

    const emailPattern = emailRegex()
    const urlPattern = urlRegex({ strict: false })
    return !emailPattern.test(value) && !value.match(urlPattern)?.some((url) => url.toLowerCase() !== params.allowedTitle.toLowerCase())
  })
